import Mustache from 'mustache';

declare global {
  interface Window {
    nhtWheelSelector: WheelData;
  }
}

export type WheelData = {
  machines: {
    [key: string]: {name: string},
  },
  productLines: {
    [key: string]: {name: string},
  }
  tyres: {
    [key: string]: {
      name: string,
      url: string,
      rimSizes: number[],
    },
  },
  products: Product[],
};

export type Product = {
  id: string;
  description: string;
  /** Url to DFF logo */
  designFromFinland: string;
  features: Feature[];
  imageAlt: string;
  imageMainUrl: string;
  /** Image conversions object */
  imageMainInfo: {
    [key: string]: {href: string},
  }
  /** Array of family codes */
  itemFamilies: string[];
  machines: Machine[];
  /** Url to MIF logo */
  madeInFinland: string;
  name: string;
  productLines: ProductLine[];
  slug: string;
  url: string;
  /** Array of work type ids */
  workTypes: string[];
};

export type Feature = {
  pid: number;
  uuid: string;
  last_updated: string;
  id: string;
  name: string;
  familyCount: number;
};

export type Machine = {
  pid: number;
  uuid: string;
  last_updated: string;
  id: string;
  name: string;
  familyCount: number;
};

export type ProductLine = {
  pid: number;
  uuid: string;
  last_updated: string;
  id: string;
  name: string;
  familyCount: number;
};


export type State = {
  productLine: string,
  machine: string,
  tyre: string,
  rimSize: string,
};

/**
 * Wheels listing element, contains filtering and rendering logic.
 *
 * Expects following html structure:

<div><!-- Container element from constructor -->
  <button data-wheels-filter-toggle>Toggle filter form</button>
  <form data-wheels-filter-form>
    <select name="productLine"></select>
    <select name="machine"></select>
    <select name="tyre"></select>
    <select name="rimSize"></select>
  </form>

  <div data-wheels-list>
    <!--Products rendered here -->
  </div>

  <script>window.nhtWheelSelector = <%+ JSON.stringify(clientSideData) %>;</script><!--Product data from Stage -->
  <script data-wheels-listing-template><!--Mustache template for products --></script>
</div>


 */
export class WheelsListing {
  #dom: {
    root: HTMLElement,
    results: HTMLElement,
    filterForm: HTMLFormElement,
    filterFormToggle: HTMLButtonElement,
    productLine: HTMLSelectElement,
    machine: HTMLSelectElement,
    tyre: HTMLSelectElement,
    rimSize: HTMLSelectElement,
  };

  values: State;

  data: WheelData;

  templates: {
    item: string,
  };

  #updatingFilters = false;

  id: string;

  constructor(container: HTMLElement, options = {}) {
    this.#dom = {
      root: container,
      results: container.querySelector('[data-wheels-list]') as HTMLElement,
      filterForm: container.querySelector('[data-wheels-filter-form]') as HTMLFormElement,
      filterFormToggle: container.querySelector('[data-wheels-filter-toggle]') as HTMLButtonElement,
      productLine: container.querySelector('[name="productLine"]') as HTMLSelectElement,
      machine: container.querySelector('[name="machine"]') as HTMLSelectElement,
      tyre: container.querySelector('[name="tyre"]') as HTMLSelectElement,
      rimSize: container.querySelector('[name="rimSize"]') as HTMLSelectElement,
    }

    this.values = {
      productLine: '',
      machine: '',
      tyre: '',
      rimSize: '',
    };

    this.id = container.id;

    // JS data unique per element
    this.data = window[`nhtWheelSelector${this.id}`];

    this.templates = {
      item: container.querySelector('[data-wheels-listing-template]')?.innerHTML || '',
    };

    if(this.data && this.#dom.filterForm && this.#dom.productLine && this.#dom.machine && this.#dom.tyre && this.#dom.rimSize) {
      // Data and required dom elements found, init rest.

      // Update selector state based on initial values
      const values = { ...this.values };
      for (let key in this.values) {
        const field = this.#dom.filterForm.querySelector(`[name="${key}"]`) as HTMLSelectElement;
        values[key] = field && field.value ? field.value : '';
      }
      this.update(values);

      this.#initListeners();

      console.log('wheels-listing.init()');
    }
  }

  /**
   * Initializes event listeners
   */
  #initListeners() {
    const changeValue = (e) => {
      // Callback was caused by internal change in filters
      if (this.#updatingFilters) {
        return;
      }

      const values = { ...this.getValues() };
      let resetFields = false;
      for (let key in values) {
        if (e.target.name === key) {
          values[e.target.name] = key === 'rimSize' ? parseFloat(e.target.value) : e.target.value;
          resetFields = true;
        }
        else if (resetFields) {
          values[key] = '';
        }
      }
      this.update(values);
      this.showResults(this.getProducts());
    };

    this.#dom.productLine?.addEventListener('change', changeValue);
    this.#dom.machine?.addEventListener('change', changeValue);
    this.#dom.tyre?.addEventListener('change', changeValue);
    this.#dom.rimSize?.addEventListener('change', changeValue);

    this.#dom.filterFormToggle?.addEventListener('click', this.#toggleFilterForm.bind(this));

    this.#dom.filterForm?.addEventListener('reset', this.reset.bind(this));
  }

  /**
   * Toggles the visibility of the filter form
   *
   * @param {Event} e - Event object
   */
  #toggleFilterForm(e) {
    this.#dom.filterForm.classList.toggle('collapsed');
    this.#dom.filterFormToggle?.classList.toggle('collapsed');
  }

  /**
   * Returns current filter values
   *
   * @returns {State} - Filter values
   */
  getValues() {
    return this.values;
  }

  /**
   * Returns associated dom elements
   *
   * @returns {object}
   */
  getElements() {
    return this.#dom;
  }

  /**
   * Updates filter state.
   *
   * @param {State} newValues New values to set.
   */
  update(newValues: State) {
    const changed: HTMLSelectElement[] = [];
    const oldValues = {...this.values};
    const values = { ...oldValues, ...newValues };

    const updateSelectOptions = (element, availableOptions) => {
      if (element.value !== values[element.name]) {
        changed.push(element);
      }
      for (const opt of element.options) {
        opt.hidden = opt.value && !availableOptions.includes(opt.value);
        opt.selected = !opt.hidden && values[element.name] === opt.value;
      }
    }

    // Update product line select
    const productLine = this.#dom.productLine;
    if(productLine) {
      if (productLine.value !== values.productLine) {
        const options = this.getProducts({})
          .map(item => item.productLines)
          .flat()
          .filter((option, index, self) => self.findIndex(option2 => option2.id === option.id) === index);
        const optionIds = options.map(option => option.id);

        updateSelectOptions(productLine, optionIds);
      }
    }

    // Update machine select
    const machine = this.#dom.machine;
    if (machine) {
      if (values.productLine && this.values.productLine !== values.productLine) {
        const options = this.getProducts({ productLine: values.productLine })
          .map(item => item.machines)
          .flat()
          .filter((option, index, self) => self.findIndex(option2 => option2.id === option.id) === index);
        const optionIds = options.map(option => option.id);

        updateSelectOptions(machine, optionIds);
      }
      else if (!values.productLine) {
        updateSelectOptions(machine, []);
      }

      machine.disabled = !values.productLine;
    }

    // Update tyre select
    const tyre = this.#dom.tyre;
    if (tyre) {
      if (values.machine && this.values.machine !== values.machine) {
        let options = this.getProducts({ productLine: values.productLine, machine: values.machine }).map(item => item.itemFamilies).flat();
        options = options.filter((val, i) => options.indexOf(val) === i);
        options = options.filter((val) => !!this.data.tyres[val]); // Don't show tyre if data not found (probably not published tyre)

        updateSelectOptions(tyre, options);
      }
      else if (!values.machine) {
        updateSelectOptions(tyre, []);
      }

      tyre.disabled = !values.productLine || !values.machine;
    }

    // Update rim size select
    const rimSize = this.#dom.rimSize;
    if (rimSize) {
      if (values.tyre && this.values.tyre !== values.tyre) {
        let options = this.data.tyres[values.tyre] ? this.data.tyres[values.tyre].rimSizes : [];
        options = options.filter((val, i) => options.indexOf(val) === i);
        const optionValues = options.map(val => String(val));

        updateSelectOptions(rimSize, optionValues);
      }
      else if (!values.tyre) {
        updateSelectOptions(rimSize, []);
      }

      rimSize.disabled = !values.productLine || !values.machine || !values.tyre;
    }

    this.values = values;

    // Trigger native event on all elements that were changed
    // Combobox needs this, if it's used
    this.#updatingFilters = true;
    changed.forEach((el) => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
    this.#updatingFilters = false;

    this.triggerEvents(values, oldValues);
  }

  /**
   * Resets all filters to empty.
   */
  reset() {
    const values = { ...this.getValues() };

    for (let key in values) {
      values[key] = '';
    }

    this.update(values);
    this.showResults(this.getProducts());
  }

  /**
   * Get array of products which match given values.
   *
   * @param {Partial<State>} values - Values to use as filters. If empty, current values are used.
   * @returns {Array<object>} - Filtered products
   */
  getProducts(values?: Partial<State>) {
    const usedValues = values || this.getValues();

    return this.data.products.filter(item => {
      let match = true;
      if (match && usedValues.productLine) {
        match = item.productLines.map(it => it.id).includes(usedValues.productLine);
      }
      if (match && usedValues.machine) {
        match = item.machines.map(it => it.id).includes(usedValues.machine);
      }
      if (match && usedValues.tyre) {
        match = item.itemFamilies.includes(usedValues.tyre);
      }
      if (match && usedValues.tyre && usedValues.rimSize) {
        const tyre = this.data.tyres[usedValues.tyre];
        match = tyre && tyre.rimSizes.includes(Number(usedValues.rimSize));
      }
      return match;
    });
  }

  /**
   * Renders the products list.
   *
   * @param {Array<Object>} products - Array of products to render.
   */
  showResults(products) {
    if(this.templates.item) {
      this.#dom.results.innerHTML = Mustache.render(this.templates.item, { products: products });
    }
  }

  /**
   * Triggers analytics events based on changed values.
   *
   * @param {State} newValues - New values
   * @param {State} oldValues - Old values
   */
  triggerEvents(newValues: State, oldValues: State) {
    // Placeholder for future analytics events for wheels listing
    // Note: child classes may use this.
  }
}

export async function init(elements: NodeListOf<HTMLElement>) {
  elements.forEach(element => {
    new WheelsListing(element);
  })
}
